import { createAccessControl } from "better-auth/plugins/access";

/**
 * Resource → action statements for Swishr Pulse.
 * Roles: admin > manager > qa > agent (hierarchical, not parallel).
 * Data-scope filtering (e.g. agent sees own records only) is enforced at the
 * query layer, not here — these permissions govern capability, not scope.
 */
export const statement = {
  organization: ["read", "update"],
  user:       ["create", "read", "update", "delete", "ban", "impersonate"],
  agent:      ["create", "read", "update", "deactivate"],
  evaluation: ["create", "read", "delete", "export"],
  dsat:       ["create", "read", "delete", "export"],
  scorecard:  ["read", "export"],
  coaching:   ["create", "read", "update"],
  quiz:       ["create", "read", "take", "manage"],
  schedule:   ["create", "read", "update", "publish"],
  wfm:        ["create", "read", "update", "delete"],
  insight:    ["read"],
  settings:   ["read", "update"],
  billing:    ["read", "manage"],
  report:     ["create", "read"],
} as const;

export const ac = createAccessControl(statement);

/** Full system access. Can use all admin plugin APIs. */
export const admin = ac.newRole({
  organization: ["read", "update"],
  user:       ["create", "read", "update", "delete", "ban", "impersonate"],
  agent:      ["create", "read", "update", "deactivate"],
  evaluation: ["create", "read", "delete", "export"],
  dsat:       ["create", "read", "delete", "export"],
  scorecard:  ["read", "export"],
  coaching:   ["create", "read", "update"],
  quiz:       ["create", "read", "take", "manage"],
  schedule:   ["create", "read", "update", "publish"],
  wfm:        ["create", "read", "update", "delete"],
  insight:    ["read"],
  settings:   ["read", "update"],
  billing:    ["read", "manage"],
  report:     ["create", "read"],
});

/** Team operations. Cannot manage users, change settings config, or access billing. */
export const manager = ac.newRole({
  organization: ["read"],
  user:       ["read"],
  agent:      ["create", "read", "update", "deactivate"],
  evaluation: ["create", "read", "export"],
  dsat:       ["create", "read", "export"],
  scorecard:  ["read", "export"],
  coaching:   ["create", "read", "update"],
  quiz:       ["create", "read"],
  schedule:   ["create", "read", "update", "publish"],
  wfm:        ["create", "read", "update", "delete"],
  insight:    ["read"],
  settings:   ["read"],
  report:     ["create", "read"],
});

/** Quality-focused. Can evaluate and analyse DSAT. No scheduling/WFM/billing. */
export const qa = ac.newRole({
  agent:      ["read"],
  evaluation: ["create", "read", "export"],
  dsat:       ["create", "read", "export"],
  scorecard:  ["read"],
  coaching:   ["create", "read", "update"],
  quiz:       ["read"],
  report:     ["create", "read"],
});

/** Self-service only. All data-scope restrictions enforced at query layer. */
export const agent = ac.newRole({
  evaluation: ["read"],
  scorecard:  ["read"],
  coaching:   ["read"],
  quiz:       ["take", "read"],
  schedule:   ["read"],
});


/**
 * Platform super-admin — cross-organization access.
 * This role is assigned to Swishr staff only and grants full permissions
 * across all organizations. It must be in adminRoles so better-auth
 * allows it to call admin API endpoints (createUser, setPassword, etc.).
 */
export const super_admin = ac.newRole({
  organization: ["read", "update"],
  user:       ["create", "read", "update", "delete", "ban", "impersonate"],
  agent:      ["create", "read", "update", "deactivate"],
  evaluation: ["create", "read", "delete", "export"],
  dsat:       ["create", "read", "delete", "export"],
  scorecard:  ["read", "export"],
  coaching:   ["create", "read", "update"],
  quiz:       ["create", "read", "take", "manage"],
  schedule:   ["create", "read", "update", "publish"],
  wfm:        ["create", "read", "update", "delete"],
  insight:    ["read"],
  settings:   ["read", "update"],
  billing:    ["read", "manage"],
  report:     ["create", "read"],
});
